import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { fromJS, List, Map } from 'immutable';
import { compact, trim, truncate } from 'lodash';
import { Appointment } from '../../../appointment-data/models/appointment.model';
import { Attachment } from '../../../attachments/attachment.model';
import { Client } from '../../../client-data/client.model';
import {
  DocumentReferenceTypesEnum,
  DocumentTypesEnum,
  MAX_LENGTH_DOCUMENT_ITEM_DESCRIPTION,
  MAX_LENGTH_DOCUMENT_ITEM_NAME,
} from '../../../document-data/document-data.data';
import { Document } from '../../../document-data/document.model';
import { Billing } from '../../../document-data/sub-models/billing.model';
import { DocumentItem } from '../../../document-data/sub-models/document-item.model';
import { Reference } from '../../../document-data/sub-models/reference.model';
import { Shipping } from '../../../document-data/sub-models/shipping.model';
import { CONTENT_KEY } from '../../../entity-data/entity.model';
import { Expense } from '../../../expense-data/models/expense.model';
import { ItemDataBuilder } from '../../../item-data/item-data.builder';
import { ItemUnitTypesEnum } from '../../../item-data/item-data.data';
import { Item } from '../../../item-data/item.model';
import { Settings } from '../../../settings-data/settings.model';
import { DocumentNotes } from '../../../settings-data/sub-models/document-preset-settings/document-notes.model';
import { DocumentPresetSettings } from '../../../settings-data/sub-models/document-preset-settings/document-preset-settings.model';
import { AmPmTimePipe } from '../../../shared/pipes/am-pm-time/am-pm-time.pipe';
import { FriendlyDatePipe } from '../../../shared/pipes/friendly-date/friendly-date.pipe';
import { DateTimeService } from 'i2g-ng-utils';
import { TrackedTime } from '../../../tracked-time-data/models/tracked-time.model';
import { IAddClientData } from '../add-client/add-client.component';
import { AchCreditBankDetails } from '../../../document-data/sub-models/ach-credit-bank-details.model';

@Injectable()
export class ContentCreateBuilder {
  constructor(
    private _itemDataBuilder: ItemDataBuilder,
    private _dateTimeService: DateTimeService,
    private _friendlyDatePipe: FriendlyDatePipe,
    private _translateService: TranslateService,
    private _amPmTimePipe: AmPmTimePipe
  ) {}

  buildItemFromDocumentItem(
    documentItem: DocumentItem,
    settings: Settings,
    productNameEnabled: boolean
  ): Item {
    const itemContentProps = {
      [Item.CONTENT_KEYS.CODE]: documentItem.code,
      [Item.CONTENT_KEYS.RATE]: documentItem.unitPrice,
      [Item.CONTENT_KEYS.UNIT_TYPE]: documentItem.unitType,
      [Item.CONTENT_KEYS.TAXABLE]: true,
    };

    if (productNameEnabled) {
      itemContentProps[Item.CONTENT_KEYS.NAME] = documentItem.name || '';
      itemContentProps[Item.CONTENT_KEYS.DETAILS] =
        documentItem.description || '';
    } else {
      itemContentProps[Item.CONTENT_KEYS.NAME] = documentItem.description || '';
    }

    return this._itemDataBuilder.buildEntity(
      {
        [Item.KEYS.LOCAL_ID]: documentItem.referenceLocalId,
        [CONTENT_KEY]: itemContentProps,
      },
      settings
    );
  }

  buildDocumentItemPropsFromItem(
    documentType: DocumentTypesEnum,
    item: Item,
    productNameEnabled: boolean
  ): Map<string, any> {
    return fromJS({
      [DocumentItem.KEYS.NAME]: productNameEnabled
        ? truncate(item.name, { length: MAX_LENGTH_DOCUMENT_ITEM_NAME })
        : null,
      [DocumentItem.KEYS.DESCRIPTION]: truncate(
        productNameEnabled ? item.details : item.name,
        {
          length: MAX_LENGTH_DOCUMENT_ITEM_DESCRIPTION,
        }
      ),
      [DocumentItem.KEYS.CODE]: item.code,
      [DocumentItem.KEYS.UNIT_PRICE]:
        documentType === DocumentTypesEnum.purchase_order
          ? item.cost || item.rate || 0
          : item.rate || 0,
      [DocumentItem.KEYS.UNIT_TYPE]: item.unitType,
      [DocumentItem.KEYS.REFERENCE]: {
        [Reference.KEYS.TYPE]: DocumentReferenceTypesEnum.product,
        [Reference.KEYS.ID]: item.id,
        [Reference.KEYS.REFERENCE_LOCAL_ID]: item.localId,
        [Reference.KEYS.REVISION_ID]: item.revisionId,
      },
    });
  }

  buildDocumentItemPropsFromExpense(
    expense: Expense,
    productNameEnabled: boolean
  ): Map<string, any> {
    const expenseDate: string = this._friendlyDatePipe.transform(
      expense.date,
      true,
      0,
      0,
      this._translateService.instant('TIME.DATE.LONG_DAY_MONTH_DAY_YEAR_FORMAT')
    ) as string;
    const expenseMerchant: string = productNameEnabled ? '' : expense.merchant;
    const description: string = trim(
      `${expenseMerchant}\n\n${expenseDate}\n\n${compact([
        expense.category,
        expense.description,
      ]).join(' - ')}`
    );

    return fromJS({
      [DocumentItem.KEYS.NAME]: productNameEnabled
        ? expense.merchant || this._translateService.instant('EXPENSE.NAME')
        : null,
      [DocumentItem.KEYS.DESCRIPTION]: truncate(description, {
        length: MAX_LENGTH_DOCUMENT_ITEM_DESCRIPTION,
      }),
      [DocumentItem.KEYS.UNIT_PRICE]: expense.total,
      [DocumentItem.KEYS.UNIT_TYPE]: ItemUnitTypesEnum.parts,
      [DocumentItem.KEYS.REFERENCE]: {
        [Reference.KEYS.TYPE]: DocumentReferenceTypesEnum.expense,
        [Reference.KEYS.ID]: expense.id,
        [Reference.KEYS.REFERENCE_LOCAL_ID]: expense.localId,
        [Reference.KEYS.REVISION_ID]: expense.revisionId,
      },
    });
  }

  buildDocumentItemPropsFromAppointment(
    appointment: Appointment,
    productNameEnabled: boolean,
    showQuantityAndRate: boolean
  ): Map<string, any> {
    const startDate = appointment.startDate;
    const endDate = this._dateTimeService.addTime(
      startDate,
      appointment.duration,
      'seconds',
      null,
      false,
      false
    );
    const startTime = this._amPmTimePipe.transform(startDate);
    const endTime = this._amPmTimePipe.transform(endDate);
    const date = this._friendlyDatePipe.transform(
      startDate,
      true,
      0,
      0,
      this._translateService.instant('TIME.DATE.LONG_DAY_MONTH_DAY_YEAR_FORMAT')
    );
    const descriptionDateAndTime = `${date}, ${startTime} - ${endTime}`;
    const qty = showQuantityAndRate ? appointment.duration / 3600 : 1;
    const unitType = ItemUnitTypesEnum.labor;
    const referenceProps = {
      [Reference.KEYS.TYPE]: DocumentReferenceTypesEnum.appointment,
      [Reference.KEYS.ID]: appointment.id,
      [Reference.KEYS.REFERENCE_LOCAL_ID]: appointment.localId,
      [Reference.KEYS.REVISION_ID]: appointment.revisionId,
    };

    return fromJS({
      [DocumentItem.KEYS.NAME]: productNameEnabled
        ? truncate(descriptionDateAndTime, {
            length: MAX_LENGTH_DOCUMENT_ITEM_NAME,
          })
        : null,
      [DocumentItem.KEYS.DESCRIPTION]: truncate(
        productNameEnabled
          ? appointment.notes
          : trim(`${descriptionDateAndTime}\n\n${appointment.notes}`),
        { length: MAX_LENGTH_DOCUMENT_ITEM_DESCRIPTION }
      ),
      [DocumentItem.KEYS.UNIT_PRICE]: 0,
      [DocumentItem.KEYS.QTY]: +qty.toFixed(4) || 1,
      [DocumentItem.KEYS.UNIT_TYPE]: unitType,
      [DocumentItem.KEYS.REFERENCE]: referenceProps,
    });
  }

  buildDocumentItemPropsFromTrackedTime(
    trackedTime: TrackedTime,
    productNameEnabled: boolean,
    showQuantityAndRate: boolean
  ): Map<string, any> {
    const startDate = trackedTime.startDate;
    const endDate = this._dateTimeService.addTime(
      startDate,
      trackedTime.duration,
      'seconds',
      null,
      false,
      false
    );
    const startTime = this._amPmTimePipe.transform(startDate);
    const endTime = this._amPmTimePipe.transform(endDate);
    const date = this._friendlyDatePipe.transform(
      startDate,
      true,
      0,
      0,
      this._translateService.instant('TIME.DATE.LONG_DAY_MONTH_DAY_YEAR_FORMAT')
    );
    const descriptionDateAndTime = `${date}, ${startTime} - ${endTime}`;
    const qty = showQuantityAndRate ? trackedTime.duration / 3600 : 1;
    const unitType = showQuantityAndRate
      ? ItemUnitTypesEnum.labor_hours
      : ItemUnitTypesEnum.labor;
    const referenceProps = {
      [Reference.KEYS.TYPE]: DocumentReferenceTypesEnum.tracked_time,
      [Reference.KEYS.ID]: trackedTime.id,
      [Reference.KEYS.REFERENCE_LOCAL_ID]: trackedTime.localId,
      [Reference.KEYS.REVISION_ID]: trackedTime.revisionId,
    };

    return fromJS({
      [DocumentItem.KEYS.NAME]: productNameEnabled
        ? truncate(descriptionDateAndTime, {
            length: MAX_LENGTH_DOCUMENT_ITEM_NAME,
          })
        : null,
      [DocumentItem.KEYS.DESCRIPTION]: truncate(
        productNameEnabled
          ? trackedTime.notes
          : trim(`${descriptionDateAndTime}\n\n${trackedTime.notes}`),
        { length: MAX_LENGTH_DOCUMENT_ITEM_DESCRIPTION }
      ),
      [DocumentItem.KEYS.UNIT_PRICE]: 0,
      [DocumentItem.KEYS.QTY]: +qty.toFixed(4) || 1,
      [DocumentItem.KEYS.UNIT_TYPE]: unitType,
      [DocumentItem.KEYS.REFERENCE]: referenceProps,
    });
  }

  buildDocumentContentPropsFromAdditionalFields(
    additionalFieldsProps: List<Map<string, any>>
  ): Map<string, any> {
    return fromJS({
      [Document.CONTENT_KEYS.SETTINGS]: {
        [DocumentPresetSettings.KEYS.CUSTOM_FIELDS]: additionalFieldsProps,
      },
    });
  }

  buildDocumentContentPropsFromNote(
    useDefault: boolean,
    documentType: DocumentTypesEnum,
    note: string
  ): Map<string, any> {
    return fromJS({
      [Document.CONTENT_KEYS.SETTINGS]: {
        [DocumentPresetSettings.KEYS.NOTES]: {
          [useDefault
            ? DocumentNotes.KEYS.DEFAULT
            : DocumentNotes.KEYS[(documentType as any).toUpperCase()]]: note,
        },
      },
    });
  }

  buildDocumentContentPropsFromPaymentDetails(
    paymentDetails: string
  ): Map<string, any> {
    return fromJS({
      [Document.CONTENT_KEYS.SETTINGS]: {
        [DocumentPresetSettings.KEYS.PAYMENT_DETAILS]: paymentDetails,
      },
    });
  }

  buildDocumentContentPropsFromDeleteClient(): Map<string, any> {
    return fromJS({
      [Document.CONTENT_KEYS.BILLING]: null,
      [Document.CONTENT_KEYS.SHIPPING]: null,
    });
  }

  buildDocumentContentPropsFromClientData(
    clientData: IAddClientData
  ): Map<string, any> {
    const { terms, billingInfo, shippingInfo, reference } = clientData;
    const documentContentProps = {};
    const documentSettingsProps = {};

    if (terms !== null) {
      documentSettingsProps[DocumentPresetSettings.KEYS.TERMS] = terms;
    }

    documentContentProps[
      Document.CONTENT_KEYS.SETTINGS
    ] = documentSettingsProps;

    if (billingInfo) {
      documentContentProps[Document.CONTENT_KEYS.BILLING] = billingInfo.set(
        Billing.KEYS.REFERENCE,
        reference
      );
    }

    if (shippingInfo) {
      documentContentProps[Document.CONTENT_KEYS.SHIPPING] = shippingInfo;
    }

    return fromJS(documentContentProps);
  }

  buildDocumentContentPropsFromClient(client: Client): Map<string, any> {
    const referenceProps = {
      [Reference.KEYS.TYPE]: DocumentReferenceTypesEnum.client,
      [Reference.KEYS.ID]: client.id,
      [Reference.KEYS.REFERENCE_LOCAL_ID]: client.localId,
      [Reference.KEYS.REVISION_ID]: client.revisionId,
    };
    const billingProps = {
      [Billing.KEYS.NAME]: client.billingName,
      [Billing.KEYS.EMAIL]: client.email,
      [Billing.KEYS.ADDRESS]: client.billingAddress,
      [Billing.KEYS.PHONE]: client.phone,
      [Billing.KEYS.MOBILE]: client.mobile,
      [Billing.KEYS.REFERENCE]: referenceProps,
    };
    const shippingProps = {
      [Shipping.KEYS.ADDRESS]: client.shippingAddress,
    };
    const documentContentProps: any = {
      [Document.CONTENT_KEYS.BILLING]: billingProps,
      [Document.CONTENT_KEYS.SHIPPING]: shippingProps,
    };

    if (client.terms !== null) {
      documentContentProps[Document.CONTENT_KEYS.SETTINGS] = {
        [DocumentPresetSettings.KEYS.TERMS]: client.terms,
      };
    }

    return fromJS(documentContentProps);
  }

  buildDocumentContentPropsFromAttachmentList(
    attachments: List<Attachment>
  ): Map<string, any> {
    return fromJS({
      [Document.CONTENT_KEYS.ATTACHMENTS]: attachments,
    });
  }

  buildClientReference(client: Client): Reference {
    return new Reference({
      [Reference.KEYS.TYPE]: DocumentReferenceTypesEnum.client,
      [Reference.KEYS.ID]: client.id,
      [Reference.KEYS.REFERENCE_LOCAL_ID]: client.localId,
      [Reference.KEYS.REVISION_ID]: client.revisionId,
    });
  }

  buildClientEmailProps(billingInfo: Map<string, any>): Map<string, any> {
    return fromJS({
      [Client.CONTENT_KEYS.EMAIL]: billingInfo.get(Billing.KEYS.EMAIL),
      [Client.CONTENT_KEYS.MOBILE]: billingInfo.get(Billing.KEYS.MOBILE),
    });
  }

  buildItemReference(item: Item): Reference {
    return new Reference({
      [Reference.KEYS.TYPE]: DocumentReferenceTypesEnum.product,
      [Reference.KEYS.ID]: item.id,
      [Reference.KEYS.REFERENCE_LOCAL_ID]: item.localId,
      [Reference.KEYS.REVISION_ID]: item.revisionId,
    });
  }

  buildAchCreditBankDetailsProps(achCreditBankDetails: AchCreditBankDetails) {
    return fromJS({
      [Document.KEYS.ACH_CREDIT_BANK_DETAILS]: achCreditBankDetails,
    });
  }
}
